import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { defaultTQC5010_R01Dto, TQC5010_R01Dto } from './Dto_TQC5010_R01';

/** 검사항목 조회 조건 */
export interface TQC5010_R01Filter {
	inspItemGroup: string;	// 검사항목그룹
	inspItemNm: string;		// 검사기준명
}

export interface Store_TQC5010_R01 {
	TQC5010_R01Filter: TQC5010_R01Filter;
	TQC5010_R01PastFilter: TQC5010_R01Filter;
	setTQC5010_R01Filter: (filter: Partial<TQC5010_R01Filter>) => void;
	setTQC5010_R01PastFilter: (filter: Partial<TQC5010_R01Filter>) => void;

	TQC5010_R01List: TQC5010_R01Dto[];
	setTQC5010_R01List: (list: TQC5010_R01Dto[]) => void;
	insertTQC5010_R01List: (data: TQC5010_R01Dto[], index?: number) => void;
	removeTQC5010_R01List: (index: number) => void;
	updateTQC5010_R01List: (index: number, data: TQC5010_R01Dto) => void;

	deletedTQC5010_R01List: string[];
	addDeletedTQC5010_R01List: (idList: string[]) => void;
	removeDeletedTQC5010_R01List: (idList?: string[]) => void;

	selectedTQC5010_R01List: string[];
	setSelectedTQC5010_R01List: (idList: string[]) => void;
	addSelectedTQC5010_R01List: (idList: string[]) => void;
	removeSelectedTQC5010_R01List: (idList?: string[]) => void;
	initSelectedTQC5010_R01List: () => void;
	allSelectedTQC5010_R01List: () => void;

	originalTQC5010_R01List: TQC5010_R01Dto[];
	setOriginalTQC5010_R01List: (list: TQC5010_R01Dto[]) => void;

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	initData: () => void;
}

/** 검색 조건 초기값 */
export const defaultTQC5010_R01Filter: TQC5010_R01Filter = {
	inspItemGroup: '',
	inspItemNm: '',
};

export const useTQC5010_R01Store = create<Store_TQC5010_R01>((set) => ({
	TQC5010_R01Filter: defaultTQC5010_R01Filter,
	TQC5010_R01PastFilter: defaultTQC5010_R01Filter,

	/** 검색 조건을 부분 갱신한다. */
	setTQC5010_R01Filter: (update) => set((state) => ({
		TQC5010_R01Filter: { ...state.TQC5010_R01Filter, ...update },
	})),

	/** 마지막 조회에 사용된 검색 조건을 저장한다. */
	setTQC5010_R01PastFilter: (update) => set((state) => ({
		TQC5010_R01PastFilter: { ...state.TQC5010_R01PastFilter, ...update },
	})),

	TQC5010_R01List: [],

	/** 조회 결과 목록을 설정하고 valueIndex·orderIndex를 보정한다. */
	setTQC5010_R01List: (TQC5010_R01List) => set((state) => {
		const newList: TQC5010_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		TQC5010_R01List.forEach((item) => {
			newList.push({
				...defaultTQC5010_R01Dto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: Math.max(item.orderIndexSeq, 0),
			});
			valueAndOrderIndex++;
		});
		return { ...state, TQC5010_R01List: newList };
	}),

	/** 지정 위치에 신규 행을 삽입하고 orderIndexSeq를 재정렬한다. */
	insertTQC5010_R01List: (data, index) => set((state) => {
		let newList = [...state.TQC5010_R01List];
		const targetRowData = newList.find((item) => item.valueIndex === index);
		const targetOrderIndex = targetRowData?.orderIndex || 0;
		const newOrderIndexSeq = (targetRowData?.orderIndexSeq || 0) + 1;
		let maxValueIndex = Math.max(0, ...newList.map((item) => item.valueIndex || 0)) + 1;

		const targetRows = newList.filter((item) => item.orderIndex === targetOrderIndex);
		targetRows.forEach((item) => {
			if (item.orderIndexSeq >= newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq + 1;
			}
		});

		newList = newList.filter((item) => item.orderIndex !== targetOrderIndex);
		newList.push(...targetRows);

		const newData = data.map((item) => ({
			...defaultTQC5010_R01Dto,
			...item,
			valueIndex: maxValueIndex++,
			orderIndex: targetOrderIndex,
			orderIndexSeq: newOrderIndexSeq,
		}));

		if (index === undefined) {
            newList.push(...newData);
        } else {
            newList.splice(index, 0, ...newData);
        }

		return { ...state, TQC5010_R01List: newList };
	}),

	/** valueIndex 기준으로 행을 목록에서 제거한다. (신규 행 전용) */
	removeTQC5010_R01List: (index) => set((state) => ({
		...state,
		TQC5010_R01List: state.TQC5010_R01List.filter((item) => item.valueIndex !== index),
	})),

	/** valueIndex에 해당하는 행 데이터를 갱신한다. */
	updateTQC5010_R01List: (valueIndex, data) => set((state) => ({
		...state,
		TQC5010_R01List: state.TQC5010_R01List.map((item) =>
			item.valueIndex === valueIndex ? data : item
		),
	})),

	deletedTQC5010_R01List: [],

	/** 서버 삭제 대상 행을 삭제 표시 목록에 추가한다. (신규·수정 행은 제외) */
	addDeletedTQC5010_R01List: (idList) => set((state) => {
		const deletedList = [...state.deletedTQC5010_R01List];
		for (const valueIndex of idList) {
			const isInserted = state.TQC5010_R01List.find(
				(item) => item.valueIndex === Number(valueIndex) && item.inserted
			);
			const isModified = state.TQC5010_R01List.find(
				(item) => item.valueIndex === Number(valueIndex) && item.isModified
			);
			if (!isInserted && !isModified) {
				deletedList.push(valueIndex);
			}
		}
		return { ...state, deletedTQC5010_R01List: deletedList };
	}),

	/** 삭제 표시 목록에서 지정 valueIndex를 제거한다. idList 미전달 시 전체 해제. */
	removeDeletedTQC5010_R01List: (idList) => set((state) => ({
		...state,
		deletedTQC5010_R01List: state.deletedTQC5010_R01List.filter(
			(value) => (idList ? !idList.includes(value) : false)
		),
	})),

	selectedTQC5010_R01List: [],

	/** 선택된 행 목록을 일괄 설정한다. */
	setSelectedTQC5010_R01List: (idList) => set({ selectedTQC5010_R01List: idList }),

	/** 선택된 행 목록에 valueIndex를 추가한다. */
	addSelectedTQC5010_R01List: (idList) => set((state) => ({
		...state,
		selectedTQC5010_R01List: [...state.selectedTQC5010_R01List, ...idList],
	})),

	/** 선택된 행 목록에서 지정 valueIndex를 제거한다. idList 미전달 시 전체 해제. */
	removeSelectedTQC5010_R01List: (idList) => set((state) => ({
		...state,
		selectedTQC5010_R01List: state.selectedTQC5010_R01List.filter(
			(value) => (idList ? !idList.includes(value) : false)
		),
	})),

	/** 선택된 행 목록을 초기화한다. */
	initSelectedTQC5010_R01List: () => set({ selectedTQC5010_R01List: [] }),

	/** 그리드의 모든 행을 선택 상태로 설정한다. */
	allSelectedTQC5010_R01List: () => set((state) => ({
		...state,
		selectedTQC5010_R01List: state.TQC5010_R01List.map((item) => item.valueIndex.toString()),
	})),

	originalTQC5010_R01List: [],

	/** 서버 조회 원본 목록을 저장하고 valueIndex·orderIndex를 재부여한다. (수정 여부 비교용) */
	setOriginalTQC5010_R01List: (TQC5010_R01List) => set((state) => {
		const newList: TQC5010_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		TQC5010_R01List.forEach((item) => {
			newList.push({
				...defaultTQC5010_R01Dto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex,
			});
			valueAndOrderIndex++;
		});
		return { ...state, originalTQC5010_R01List: newList };
	}),

	columnState: null,

	/** 그리드 컬럼 표시 상태(너비·순서 등)를 저장한다. */
	setColumnState: (columnState) => set({ columnState }),

	scrollTop: null,

	/** 그리드 세로 스크롤 위치를 저장한다. null이면 최상단으로 이동한다. */
	setScrollTop: (scrollTop) => set({ scrollTop }),

	allCount: 0,

	/** 검색 조건에 해당하는 전체 건수를 저장한다. */
	setAllCount: (allCount) => set({ allCount }),

	/** 화면 상태(검색 조건·목록·선택·삭제 표시)를 초기값으로 되돌린다. */
	initData: () => set((state) => ({
		...state,
		TQC5010_R01Filter: defaultTQC5010_R01Filter,
		TQC5010_R01PastFilter: defaultTQC5010_R01Filter,
		TQC5010_R01List: [],
		selectedTQC5010_R01List: [],
		deletedTQC5010_R01List: [],
		allCount: 0,
		scrollTop: null,
	})),
}));
